"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseUnknownToString = void 0;
exports.extractValueFromObject = extractValueFromObject;
exports.extractValueFromObjectAsString = extractValueFromObjectAsString;
const jsonpath_plus_1 = require("jsonpath-plus");
const json_1 = require("../../utils/json");
/**
 * Parses an unknown value to a string representation
 * This is used for any evaluation variable that needs string conversion
 */
const parseUnknownToString = (value) => {
    if (value === null || value === undefined) {
        return "";
    }
    if (typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean") {
        return value.toString();
    }
    if (typeof value === "object") {
        return JSON.stringify(value);
    }
    if (typeof value === "symbol") {
        return value.toString();
    }
    return String(value);
};
exports.parseUnknownToString = parseUnknownToString;
/**
 * Recursively parses JSON strings that may have been encoded multiple times.
 * This handles cases where data has been JSON.stringify'd multiple times.
 *
 * @param value - The potentially multi-encoded JSON string
 * @returns The final parsed object or the original value if parsing fails
 */
function parseMultiEncodedJson(value) {
    if (typeof value !== "string") {
        return value;
    }
    const parsed = (0, json_1.parseJsonPrioritised)(value);
    // If parsing fails, parseJsonPrioritised returns the original string.
    // Stop here to avoid recursing forever on plain strings.
    if (parsed === value || parsed === undefined) {
        return value;
    }
    // If result is still a string, it might be double-encoded - recurse
    if (typeof parsed === "string") {
        return parseMultiEncodedJson(parsed);
    }
    return parsed;
}
function parseJsonDefault(selectedColumn, jsonSelector) {
    // JSONPath can only query objects/arrays — return primitives as-is
    if (typeof selectedColumn !== "object" || selectedColumn === null) {
        return selectedColumn;
    }
    const result = (0, jsonpath_plus_1.JSONPath)({
        path: jsonSelector,
        json: selectedColumn, // JSONPath accepts unknown but types are strict
        eval: false,
    });
    if (!Array.isArray(result) || result.length === 0) {
        return undefined;
    }
    // For single-match queries (e.g. $.name), return the unwrapped value.
    // For multi-match queries (e.g. $[1:], $[*].name), return the full array.
    return result.length === 1 ? result[0] : result;
}
function extractValueFromObject(obj, selectedColumnId, jsonSelector, parseJson) {
    const selectedColumn = obj[selectedColumnId];
    const jsonParser = parseJson || parseJsonDefault;
    let jsonSelectedColumn;
    let error = null;
    if (jsonSelector && selectedColumn) {
        // Only parse multi-encoded JSON when a selector is present — avoids
        // mutating formatting (e.g. whitespace) for the no-selector passthrough.
        const parsed = typeof selectedColumn === "string"
            ? parseMultiEncodedJson(selectedColumn)
            : selectedColumn;
        try {
            jsonSelectedColumn = jsonParser(parsed, jsonSelector);
        }
        catch (err) {
            error =
                err instanceof Error
                    ? err
                    : new Error("There was an unknown error parsing the JSON");
            jsonSelectedColumn = selectedColumn; // Fallback to raw original value
        }
    }
    else {
        jsonSelectedColumn = selectedColumn;
    }
    return { value: jsonSelectedColumn, error };
}
/**
 * Backwards-compatible extraction for UI prompt previews and LLM prompt
 * rendering. `extractValueFromObject` preserves typed values for code eval;
 * this wrapper keeps the previous string-only behavior for prompt surfaces.
 */
function extractValueFromObjectAsString(obj, selectedColumnId, jsonSelector, parseJson) {
    const { value, error } = extractValueFromObject(obj, selectedColumnId, jsonSelector, parseJson);
    return { value: (0, exports.parseUnknownToString)(value), error };
}
